'use client';

/**
 * VCardDownloadButton — Client Component that triggers a vCard download
 * via `Blob + URL.createObjectURL` (the universal, CSP-safe download
 * mechanism).
 *
 * Spec ref: SPEC-NEGOCIO §1.3 (Descargar Contacto — vCard con todos los
 *           datos), RFC 2426 (vCard 3.0).
 *
 * AUDIT-6-008 (SPEC-046 QA AUDIT R1 COFOUNDER): the public profile page
 * (`/p/[username]`) previously used `<a href="data:text/vcard;...">` to
 * trigger the vCard download. The `data:` URL scheme is blocked by many
 * browser CSPs (and Safari blocks navigation to `data:` URLs in some
 * versions), so the download could silently fail on the public profile.
 * The private Virtual Card (`components/profile/virtual-card.tsx`) already
 * uses `downloadVCard()` from `lib/vcard.ts` (Blob + URL.createObjectURL)
 * — this component brings the public profile in line with that pattern.
 *
 * The `/p/[username]` page is a Server Component (SSR for SEO + ISR), so
 * the download logic MUST live in a Client Component child (Blob +
 * URL.createObjectURL require `document`, which is browser-only). The
 * Server Component passes the full `profile` props down; this Client
 * Component calls `downloadVCard(profile)` on click.
 */

import { type ReactNode } from 'react';
import { Download } from 'lucide-react';
import { downloadVCard } from '@/lib/vcard';
import type { VirtualCardProps } from '@/components/profile/virtual-card';

interface VCardDownloadButtonProps {
  /** Profile data to embed in the .vcf file (matches generateVCard shape). */
  profile: VirtualCardProps;
  /** Optional className override for the button. */
  className?: string;
  /** Optional children — defaults to a Spanish "Descargar vCard" label. */
  children?: ReactNode;
  /** Optional aria-label override (defaults to "Descargar vCard"). */
  ariaLabel?: string;
}

export function VCardDownloadButton({
  profile,
  className,
  children,
  ariaLabel,
}: VCardDownloadButtonProps) {
  const handleDownload = () => {
    // downloadVCard() generates the vCard string, wraps it in a Blob,
    // creates a temporary object URL, clicks a hidden <a download>, then
    // revokes the URL. This works in all modern browsers + is CSP-safe
    // (no `data:` URL navigation, no inline script).
    downloadVCard(profile);
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      className={className}
      aria-label={ariaLabel ?? 'Descargar vCard'}
    >
 {/* ÁREA C AUDIT-V25-005 (2026-08-22): replaced 📝 emoji with
          `<Download />` lucide icon for design-system consistency. */}
      {children ?? <><Download className="size-4 inline" aria-hidden="true" /> Descargar vCard</>}
    </button>
  );
}
